import React from 'react'
import { observer } from 'mobx-react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetHeaderDescription,
  ModalSheetTitle,
} from 'components/ModalSheet'
import NumericSpinnerInput from 'components/inputs/NumericSpinnerInput'
import Button from 'components/Button'
import cx from 'classnames'
import BookkeepingCorrectCycleDialogViewModel, {
  CorrectWorkItemViewModel,
} from './BookkeepingCorrectCycleDialogViewModel'
import styles from './BookkeepingCorrectCycleDialog.sass'

interface BookkeepingCorrectCycleDialogProps {
  vm: BookkeepingCorrectCycleDialogViewModel
}

export default observer(function BookkeepingCorrectCycleDialog({
  vm,
}: BookkeepingCorrectCycleDialogProps) {
  return (
    <ModalSheet
      showing={vm.showing}
      onRequestClose={vm.submit.pending ? undefined : vm.close}
    >
      <ModalSheetHeader shellSize="sm" withCloseButton></ModalSheetHeader>
      <ModalSheetContent>
        <ModalSheetContentBlock>
          <ModalSheetTitle large>
            Make corrections to cycle {vm.selectedCycle.number}
          </ModalSheetTitle>
          <ModalSheetHeaderDescription>
            Review and adjust the amount completed during this cycle.
          </ModalSheetHeaderDescription>
        </ModalSheetContentBlock>
        <ModalSheetContentBlock />
      </ModalSheetContent>
      <ModalSheetContent>
        <WorkItemsHeaders count={vm.correctedItems.length} />
        {vm.correctedItems.map((ci) => (
          <WorkItemRow key={ci.workItemId} vm={ci} />
        ))}
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            type="default"
            onClick={vm.close}
            disabled={vm.submit.pending}
            data-test="correct-cycle-close-button"
          >
            Close
          </Button>
          <Button
            type="primary"
            data-test="correct-cycle-submit-button"
            onClick={vm.submit}
            loading={vm.submit.pending}
            disabled={vm.submit.pending}
          >
            Correct Cycle
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})

interface WorkItemsHeaderProps {
  count: number
}

const WorkItemsHeaders = observer(function WorkItemsHeader({
  count,
}: WorkItemsHeaderProps) {
  return (
    <div className={styles.workItemsHeadersContainer}>
      <div className={cx(styles.workItemHeader, styles.service)}>
        Services Items{' '}
        <span className={styles.serviceItemHeaderCount}>({count})</span>
      </div>
      <div className={cx(styles.workItemHeader, styles.completed)}>
        Completed
      </div>
      <div className={cx(styles.workItemHeader, styles.corrected)}>
        Correct amount
      </div>
    </div>
  )
})

interface WorkItemRowProps {
  vm: CorrectWorkItemViewModel
}

const WorkItemRow = observer(function WorkItemRow({ vm }: WorkItemRowProps) {
  return (
    <div
      className={styles.workItemRow}
      data-test={`correct-cycle-work-item-${vm.name}`}
    >
      <div
        className={cx(
          styles.workItemCell,
          styles.service,
          styles.serviceContainer,
        )}
      >
        <div className={styles.serviceName}>{vm.name}</div>
        <div className={styles.serviceDescription}>{vm.description}</div>
      </div>
      <div
        className={cx(styles.workItemCell, styles.completed)}
        data-test={`correct-cycle-remaining-${vm.name}`}
      >
        {vm.completedCount}
      </div>
      <div className={cx(styles.workItemCell, styles.corrected)}>
        <NumericSpinnerInput
          min={vm.fieldLengths.correctAmount.min}
          max={vm.maxCorrectAmount}
          value={vm.correctAmount}
          invalid={Boolean(vm.validation.getError('correctAmount'))}
          onChange={(val) => vm.setCorrectedItems(val)}
          data-test={`correct-cycle-work-item-input-${vm.name}`}
        />
      </div>
    </div>
  )
})
